import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiPost } from '@lib/apiClient';
import { useEligibleQueue } from '@hooks/useEligibleQueue';
import { useIntakeConfig } from '@hooks/useIntakeConfig';
import { useBatch } from '@hooks/useBatch';
import { QueueTile } from '@components/QueueTile';
import { ScanInput } from '@components/ScanInput';
import { StageCard } from '@components/StageCard';
import type { ReadAs } from '@components/ConfirmationCard';
import { fmtMemberId } from '@lib/member-id';
import { clientLabel, clientSortKey } from '@lib/client-key';
import { identifierSpaceLabel } from '@lib/token-space';
import type { Eligibility, LookupResult, MemberKit, NearMiss, QueueItem } from '@/types/intake';

import { useCampaign } from '@hooks/useCampaign';

interface Selection {
  kit: MemberKit;
  eligibility: Eligibility;
  nearMiss: NearMiss | null;
  /**
   * The USPS tracking number scanned during THIS identification attempt, if any. Carried to the
   * server so the cross-client guard has a physical piece to check the filing against. It survives
   * a follow-up typed barcode on purpose: scanning another plan's label, finding nothing, then
   * typing a barcode that matches a same-accession member of THIS plan is exactly the mistake the
   * guard exists to catch. Cleared when the member is added or the card is cancelled.
   */
  scannedTracking: string | null;
  /**
   * How this member was identified. A scanned carrier label cannot be mistyped and a queue pick was
   * never keyed at all, so the cross-plan near-miss warning is only meaningful for a typed barcode.
   */
  identifiedBy: 'tracking' | 'typed' | 'queue';
  /**
   * What was entered, and which identifier space the server read it in. Shown on the confirmation
   * card so a value read off a kit that was looked up as a MEMBER ID is visible rather than silent.
   * Null for a queue pick, where nothing was keyed. See lib/token-space.ts for why this is a
   * control and not a nicety.
   */
  readAs: ReadAs | null;
}

/**
 * The resulting bench: identify a member (scan/type or queue pick), capture the up-front consent
 * photo, and ADD them to a batch. Selected members accumulate below; "Move all to testing" sends the
 * batch to the Testing tab where results are set. No result is chosen here.
 */
export function Bench() {
  const { campaign: CAMPAIGN } = useCampaign();
  const qc = useQueryClient();
  const nav = useNavigate();
  const cfg = useIntakeConfig(CAMPAIGN);
  const queue = useEligibleQueue(CAMPAIGN);
  const batch = useBatch(CAMPAIGN);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [candidates, setCandidates] = useState<MemberKit[]>([]);
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Survives re-renders without causing one; see Selection.scannedTracking for why it is kept.
  const scannedTracking = useRef<string | null>(null);

  const pick = (
    kit: MemberKit,
    eligibility: Eligibility,
    nearMiss: NearMiss | null,
    identifiedBy: Selection['identifiedBy'] = 'typed',
    readAs: ReadAs | null = null,
  ) => {
    setCandidates([]);
    setNotice(null);
    setSelection({ kit, eligibility, nearMiss, scannedTracking: scannedTracking.current, identifiedBy, readAs });
  };

  const lookup = async (token: string) => {
    setBusy(true);
    setNotice(null);
    setCandidates([]);
    try {
      const res = await apiPost<LookupResult>('/api/intake/kit-lookup', { campaign: CAMPAIGN, token });
      if (!res.ok) {
        setNotice((res.data.error as string) || 'Lookup failed');
        return;
      }
      const data = res.data as unknown as LookupResult;
      // Remember a scanned carrier label for the rest of this identification attempt.
      if (data.tokenType === 'usps-tracking') scannedTracking.current = token;
      // Halt on ambiguity: never auto-pick when the number is held by more than one health plan.
      // A kit barcode carries no plan segment, so the same number really does exist in several
      // plans, and the local database can only ever show this plan's copy of it.
      // Two DIFFERENT halts, and neither may be folded into the other. This one fires when a 6 or
      // 7 digit value names a kit AND a member: same plan, two identifier spaces. The one below
      // fires when a number is held by more than one health plan. A value can trip either.
      // A typed 5-digit number that could name more than one kit. Checked FIRST, and nothing is
      // offered to choose from: each kit would look like an ordinary card with a real name on it,
      // and the paper's 5 digits cannot tell them apart. Only the full return label can.
      if (data.needsReturnLabelScan) {
        setCandidates([]);
        setNotice(
          `Stop: ${token} could belong to more than one kit. Do not type it. Scan the full USPS barcode on this kit's return label instead.`,
        );
        return;
      }
      if (data.tokenSpaceAmbiguous) {
        setCandidates(data.candidates ?? []);
        setNotice(
          `Stop: ${token} is both a kit barcode and a member ID here. Check the number on the kit in your hand, then choose below.`,
        );
        return;
      }
      if (data.clientAmbiguous) {
        setCandidates(data.candidates ?? []);
        const others = data.otherPlanLabels ?? [];
        setNotice(
          others.length
            ? `Stop: this number is also held by ${others.join(' and ')}. Confirm which kit is in your hand before choosing.`
            : 'This number matches more than one health plan. Choose the right member below.',
        );
        return;
      }
      if (data.candidate) {
        pick(data.candidate, data.eligibility, data.nearMiss, data.tokenType === 'usps-tracking' ? 'tracking' : 'typed', {
          token,
          // The space it was actually resolved in, falling back to the classification when the
          // server is older than this build and does not send one.
          tokenType: data.resolvedIn ?? data.tokenType,
        });
      } else if (data.candidates && data.candidates.length > 0) {
        setCandidates(data.candidates);
      } else {
        // Name the space it searched. "No matching kit or member" alone hides the case that
        // matters: a kit barcode read as a member id finds nothing FOR THAT REASON, and the
        // technician cannot tell that from a kit that is genuinely not on file.
        setNotice(
          `No match for ${token}, read as a ${identifierSpaceLabel(data.resolvedIn ?? data.tokenType)}.`,
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const added = () => {
    scannedTracking.current = null;
    setSelection(null);
    void qc.invalidateQueries({ queryKey: ['intake-batch', CAMPAIGN] });
    void qc.invalidateQueries({ queryKey: ['intake-queue', CAMPAIGN] });
  };

  // Pull the latest queue from the campaign .db (refreshed upstream from USPS by the portal). The
  // db-cache reloads when the .db's modified-time changes, so newly-arrived samples surface here.
  const pullLatest = () => {
    void qc.invalidateQueries({ queryKey: ['intake-queue', CAMPAIGN] });
    void qc.invalidateQueries({ queryKey: ['intake-batch', CAMPAIGN] });
  };

  const moveAllToTesting = async () => {
    setMoving(true);
    setNotice(null);
    try {
      const res = await apiPost('/api/intake/batch/transition', { campaign: CAMPAIGN, from: 'staged', to: 'testing' });
      if (!res.ok) {
        setNotice((res.data.error as string) || 'Could not move the batch');
        return;
      }
      void qc.invalidateQueries({ queryKey: ['intake-batch', CAMPAIGN] });
      nav('/testing');
    } finally {
      setMoving(false);
    }
  };

  if (cfg.isLoading || !cfg.data) {
    return (
      <div className="li-page">
        <div className="li-narrow li-state">Loading...</div>
      </div>
    );
  }
  if (!cfg.data.enabled) {
    return (
      <div className="li-page">
        <div className="li-narrow li-state">Intake is not enabled for this campaign.</div>
      </div>
    );
  }

  if (selection) {
    return (
      <StageCard
        campaign={CAMPAIGN}
        kit={selection.kit}
        eligibility={selection.eligibility}
        nearMiss={selection.nearMiss}
        config={cfg.data}
        scannedTracking={selection.scannedTracking}
        identifiedBy={selection.identifiedBy}
        readAs={selection.readAs}
        onAdded={added}
        onCancel={() => {
          scannedTracking.current = null;
          setSelection(null);
        }}
      />
    );
  }

  const totals = queue.data?.totals;
  const staged = batch.data?.staged ?? [];

  return (
    <div className="li-page">
      <div className="li-narrow">
        <div className="li-head flex items-end justify-between gap-3">
          <div>
            <div className="section-eyebrow">Resulting - 26 FOBT</div>
            <h2 className="section-title">Bench</h2>
          </div>
          <button
            type="button"
            onClick={pullLatest}
            disabled={queue.isFetching || batch.isFetching}
            className="btn btn-ghost btn-sm disabled:opacity-50"
          >
            {queue.isFetching || batch.isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {totals && (
          <div className="mb-4 grid grid-cols-4 gap-2">
            {[
              { label: 'Arrived', value: totals.arrived },
              { label: 'Resulted', value: totals.resulted },
              { label: 'In progress', value: totals.pending },
              { label: 'Remaining', value: totals.remaining },
            ].map((s) => (
              <div key={s.label} className="stat" style={{ padding: '10px 12px' }}>
                <div className="value tab-num" style={{ fontSize: 22 }}>
                  {s.value.toLocaleString()}
                </div>
                <div className="label" style={{ marginBottom: 0, marginTop: 2 }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        )}

        {staged.length > 0 && (
          <div className="card mb-4">
            <div className="block-head" style={{ marginTop: 0 }}>
              Selected for testing
              <span className="aux tab-num">{staged.length}</span>
            </div>
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                onClick={() => void moveAllToTesting()}
                disabled={moving}
                className="btn-bench px-5 disabled:opacity-50"
                style={{ minHeight: 44 }}
              >
                {moving ? 'Moving...' : 'Move all to testing'}
              </button>
            </div>
            <ul className="li-list">
              {[...staged]
                .sort((a, b) => clientSortKey(a.client).localeCompare(clientSortKey(b.client)))
                .map((r) => (
                  <li key={r.recordId} className="li-list-row">
                    <span>
                      <span className="block font-medium text-cdx-fg-strong">{r.memberNameSnapshot || '(no name)'}</span>
                      <span className="block text-xs" style={{ color: 'var(--ink-400)' }}>
                        Member {fmtMemberId(r.memberId)} - {clientLabel(r.client)}
                      </span>
                    </span>
                    <span className="li-mono">{r.kitBarcode}</span>
                  </li>
                ))}
            </ul>
          </div>
        )}

        <QueueTile
          count={queue.data?.count ?? 0}
          items={queue.data?.items ?? []}
          loading={queue.isLoading}
          onSelect={(item: QueueItem) => pick(item, 'eligible', null, 'queue')}
        />
        <ScanInput onToken={(t) => void lookup(t)} busy={busy} />
        {notice && (
          <div className="note warn mt-3">{notice}</div>
        )}
        {candidates.length > 0 && (
          <ul className="li-list mt-3">
            {candidates.map((c) => (
              <li key={`${c.kitBarcode}-${c.memberId}`}>
                <button type="button" onClick={() => void lookup(c.kitBarcode)} className="li-list-row">
                  <span>
                    <span className="block font-medium text-cdx-fg-strong">{c.memberName || '(no name)'}</span>
                    <span className="block text-xs" style={{ color: 'var(--ink-400)' }}>
                      Member {fmtMemberId(c.memberId)} - {clientLabel(c.client)}
                    </span>
                  </span>
                  <span className="li-mono">{c.kitBarcode}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
